import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { CommonModule } from '@angular/common';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import {
  faEye,
  faEyeSlash,
  faTrash,
  faTrashCan,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DEPARTMENTS } from '../../../core/constants/client.constants';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';

@Component({
  selector: 'app-contacts-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DropdownModule,
    InputTextModule,
    CheckboxModule,
    CalendarModule,
    AccordionModule,
    CommonModule,
    ClientsTableComponent,
    FontAwesomeModule,
    MultiSelectModule,
    InputTextareaModule,
    AutoAuditDirective,
  ],
  templateUrl: './contacts-form.component.html',
  styleUrl: './contacts-form.component.scss',
})
export class ContactsFormComponent implements OnChanges {
  @Input() clientId: any;
  @Input() snapshotData: any[] | null = null;
  @Input() snapshotUsers: any[] | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();

  showPassword = false;

  fieldAuditData: LatestPerFieldMap = {};

  contactsForm: FormGroup;
  contactForm: FormGroup;
  isContactModalVisible = false;
  isDeleteContactModalVisible = false;
  editingContactIndex: number | undefined = undefined;
  configContactsTable: any = {
    headers: [
      { name: 'Nombre', key: 'name' },
      { name: 'Teléfono', key: 'phones' },
      { name: 'Correo electrónico', key: 'emails' },
      { name: 'Departamento(s)', key: 'departments' },
      { name: 'Idioma de contacto', key: 'language' },
      { name: 'Principal', key: 'isMainContact' },
      { name: 'Observaciones', key: 'observations' },
      { name: '', key: 'delete' },
    ],
    data: [],
    isContacts: true,
  };
  deleteContactIndex: number;
  configUsersTable: any = {
    headers: [
      { name: 'Contacto', key: 'contact' },
      { name: 'Usuario', key: 'username' },
      { name: 'Correo electrónico', key: 'mail' },
      { name: 'Observaciones', key: 'observations' },
      { name: '', key: 'delete' },
    ],
    data: [],
  };
  usersForm: FormGroup;
  userForm: FormGroup;
  isUserModalVisible = false;
  isDeleteUserModalVisible = false;
  editingUserIndex: number | undefined = undefined;
  deleteUserIndex: number;

  departmentsList = DEPARTMENTS;
  availableEmails: { label: string; value: string }[] = [];

  accessToOptions = [
    { label: 'Impuestos laboral', value: 'impuestos_laboral' },
    { label: 'Impuestos fiscal', value: 'impuestos_fiscal' },
    { label: 'Cuentas anuales', value: 'cuentas_anuales' },
    { label: 'Facturas', value: 'facturas' },
    { label: 'Suplidos', value: 'suplidos' },
    { label: 'Contratos', value: 'contratos' },
    { label: 'Nóminas', value: 'nominas' },
    { label: 'Seguros Sociales', value: 'seguros_sociales' },
    { label: 'Certificados laborales', value: 'certificados_laborales' },
    { label: 'Afiliaciones', value: 'afiliaciones' },
    { label: 'Recibos de cotización', value: 'recibos_cotizacion' },
    {
      label: 'Certificados de ingresos y retenciones',
      value: 'certificados_ingresos_retenciones',
    },
    { label: 'Certificados de retención', value: 'certificados_retencion' },
    { label: 'Informes', value: 'informes' },
  ];

  laborModelsOptions: { label: string; value: number }[] = [];
  fiscalModelsOptions: { label: string; value: number }[] = [];

  groupAccessOptions = [
    { label: 'Clientes cuota', value: 'clientes_cuota' },
    { label: 'Clientes renta', value: 'clientes_renta' },
  ];

  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faTrash = faTrashCan;

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private auditService: AuditService,
  ) {
    this.contactsForm = this.fb.group({
      contacts: this.fb.array([]),
    });

    this.contactForm = this.fb.group({
      id: [],
      name: ['', Validators.required],
      language: [''],
      phones: this.fb.array([]),
      departments: this.fb.array([]),
      observations: [''],
      isMainContact: [false],
    });

    this.contactsForm.valueChanges.subscribe(() => {
      if (this.contactsForm.dirty) {
        this.formIsDirty.emit(true);
      }
    });

    this.usersForm = this.fb.group({
      users: this.fb.array([]),
    });
    this.userForm = this.fb.group({
      id: [],
      username: ['', Validators.required],
      password: ['', Validators.required],
      mail: ['', Validators.email],
      contact: [''],
      hasAccess: [false],
      accessTo: [[]],
      laborModelsAccess: [[]],
      fiscalModelsAccess: [[]],
      accessToCircularLetters: [false],
      circularMail: [''],
      groupAccess: [[]],
      newsletterAccess: [false],
      observations: [''],
    });

    this.userForm
      .get('contact')
      ?.valueChanges.subscribe((selectedContactId) => {
        if (selectedContactId) {
          // Use configContactsTable.data which has the original structure from backend
          const contact = this.configContactsTable.data.find(
            (c) => c.id === selectedContactId,
          );

          // Only generate username for new users
          if (this.editingUserIndex === undefined && contact && contact.name) {
            const names = contact.name.trim().split(/\s+/);
            const initials = names
              .map((n) => n[0])
              .join('')
              .toLowerCase()
              .slice(0, 3);
            const randomNumbers = Math.floor(10000 + Math.random() * 90000);
            const generatedUsername = `${initials}${randomNumbers}`;
            this.userForm.patchValue(
              { username: generatedUsername },
              { emitEvent: false },
            );
          }

          // Extract emails from selected contact's departments (for both new and edit)
          if (
            contact &&
            contact.departments &&
            Array.isArray(contact.departments)
          ) {
            this.availableEmails = contact.departments
              .filter((dep) => dep && dep.mail)
              .map((dep) => ({
                label: dep.mail,
                value: dep.mail,
              }));
            // Mail del usuario = primer correo del contacto relacionado
            if (this.availableEmails.length > 0 && this.editingUserIndex === undefined) {
              this.userForm.patchValue(
                { mail: this.availableEmails[0].value },
                { emitEvent: false },
              );
            }
          } else {
            this.availableEmails = [];
          }
        } else {
          this.availableEmails = [];
        }
      });
    this.usersForm.valueChanges.subscribe(() => {
      if (this.usersForm.dirty) {
        this.formIsDirty.emit(true);
      }
    });
  }

  async ngOnInit() {
    await this.loadModels();
    if (!this.snapshotData) {
      await this.getData();
    }
    if (!this.snapshotUsers) {
      await this.getUsersData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.configContactsTable = { ...this.configContactsTable, data: [...this.snapshotData] };
      } else if (!changes['snapshotData'].firstChange) {
        void this.getData();
      }
    }

    if (changes['snapshotUsers']) {
      if (this.snapshotUsers) {
        const enriched = this.snapshotUsers.map((user) => {
          const contactId = user.contact?.id ?? user.contact;
          const firstMail = contactId ? this.getFirstContactMail(contactId) : undefined;
          return { ...user, mail: firstMail ?? user.mail };
        });
        this.configUsersTable = { ...this.configUsersTable, data: enriched };
      } else if (!changes['snapshotUsers'].firstChange) {
        void this.getUsersData();
      }
    }
  }

  async loadModels() {
    try {
      const modelsGrouped =
        await this.clientsService.getModelsGroupedByDepartment();

      // Filtrar modelos laborales
      const laborGroup = modelsGrouped.find(
        (group) => group.department === 'LABOR',
      );
      if (laborGroup && laborGroup.models) {
        this.laborModelsOptions = laborGroup.models
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((model) => ({
            label: model.name,
            value: model.id,
          }));
      }

      // Filtrar modelos fiscales y contables
      const fiscalGroups = modelsGrouped.filter(
        (group) => group.department === 'FISCAL' || group.department === 'ACCOUNTING'
      );
      const fiscalModels = fiscalGroups.flatMap((group) => group.models ?? []).sort((a, b) => a.name.localeCompare(b.name));
      if (fiscalModels.length) {
        this.fiscalModelsOptions = fiscalModels.map((model) => ({
          label: model.name,
          value: model.id,
        }));
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  }

  get activeContactsTableConfig(): any {
    if (!this.snapshotData) return this.configContactsTable;
    return {
      ...this.configContactsTable,
      headers: this.configContactsTable.headers.filter(
        (h: any) => h.key !== 'delete',
      ),
    };
  }

  get activeUsersTableConfig(): any {
    if (!this.snapshotUsers) return this.configUsersTable;
    return {
      ...this.configUsersTable,
      headers: this.configUsersTable.headers.filter(
        (h: any) => h.key !== 'delete',
      ),
    };
  }

  get contacts(): FormArray {
    return this.contactsForm.get('contacts') as FormArray;
  }

  async getData() {
    if (this.clientId) {
      const data = await this.clientsService.getClientContacts(this.clientId);
      this.contacts.clear();

      data.forEach((contact) => {
        this.contacts.push(
          this.fb.group({
            id: [contact.id],
            name: [contact.name, Validators.required],
            language: [contact.language],
            phones: this.fb.array(
              contact.phones?.map((p) => this.fb.control(p)) || [],
            ),
            departments: this.fb.array(
              contact.departments?.map((dep) =>
                this.fb.group({
                  id: [dep.id],
                  department: [dep.department, Validators.required],
                  mail: [dep.mail, [Validators.required, Validators.email]],
                }),
              ) || [],
            ),
            observations: [contact.observations],
            isMainContact: [contact.isMainContact || false],
          }),
        );
      });

      this.configContactsTable.data = data;

      try {
        this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_contact');
      } catch {
        this.fieldAuditData = {};
      }
    }
  }

  openContactModal(index?: number) {
    this.submitError = '';
    this.editingContactIndex = index;

    if (index !== undefined) {
      const contact = this.snapshotData
        ? this.configContactsTable.data[index]
        : this.contacts.at(index).value;
      this.contactForm.reset();

      this.contactForm.setControl(
        'phones',
        this.fb.array(contact.phones?.map((p) => this.fb.control(p)) || []),
      );

      const isMain = contact.isMainContact || false;
      this.contactForm.setControl(
        'departments',
        this.fb.array(
          contact.departments?.map((dep) =>
            this.fb.group({
              id: [dep.id],
              department: [dep.department, isMain ? [] : Validators.required],
              mail: [dep.mail, [Validators.required, Validators.email]],
            }),
          ) || [],
        ),
      );

      this.contactForm.patchValue(contact);
    } else {
      this.contactForm.reset();
      this.contactForm.setControl('phones', this.fb.array([]));
      this.contactForm.setControl('departments', this.fb.array([]));
      this.contactForm.patchValue({ isMainContact: false });
    }

    this.isContactModalVisible = true;
    if (this.snapshotData) {
      this.contactForm.disable();
    }
  }

  get departments(): FormArray {
    return this.contactForm.get('departments') as FormArray;
  }

  updateDepartmentValidations(isMainContact: boolean) {
    const departments = this.departments;
    for (let i = 0; i < departments.length; i++) {
      const depControl = departments.at(i).get('department');
      if (isMainContact) {
        depControl?.clearValidators();
      } else {
        depControl?.setValidators([Validators.required]);
      }
      depControl?.updateValueAndValidity();
    }
  }

  addDepartment() {
    const isMainContact = this.contactForm.get('isMainContact')?.value;
    this.departments.push(
      this.fb.group({
        id: [null],
        department: ['', isMainContact ? [] : Validators.required],
        mail: ['', [Validators.required, Validators.email]],
      }),
    );
  }

  removeDepartment(index: number) {
    this.departments.removeAt(index);
  }

  get phones(): FormArray {
    return this.contactForm.get('phones') as FormArray;
  }

  addPhone() {
    this.phones.push(this.fb.control(''));
  }

  removePhone(index: number) {
    this.phones.removeAt(index);
  }

  closeContactModal() {
    this.submitError = '';
    this.isContactModalVisible = false;
    this.editingContactIndex = undefined;
    this.contactForm.reset();
  }

  /** Guarda de envío: un doble clic crearía duplicados. */
  isSubmitting = false;
  /** Último error de guardado o borrado de un modal, para mostrarlo al usuario. */
  submitError = '';

  async saveContact() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSaveContact();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSaveContact() {
    if (this.contactForm.invalid) return;
    const value = this.contactForm.value;
    value.name = value.name ? value.name.toUpperCase() : value.name;
    value.client = Number(this.clientId);
    value.phones = Array.isArray(value.phones) ? value.phones : [];
    value.departments = Array.isArray(value.departments) ? value.departments : [];

    if (this.editingContactIndex !== undefined) {
      const contactGroup = this.contacts.at(
        this.editingContactIndex,
      ) as FormGroup;

      contactGroup.setControl(
        'departments',
        this.fb.array(value.departments.map((dep) => this.fb.group(dep))),
      );

      contactGroup.setControl(
        'phones',
        this.fb.array(value.phones.map((p) => this.fb.control(p))),
      );

      contactGroup.patchValue({
        id: value.id,
        name: value.name,
        language: value.language,
        observations: value.observations,
        isMainContact: value.isMainContact,
      });

      const raw = contactGroup.getRawValue();
      const payload = {
        ...raw,
        departments: (raw.departments || []).map((dep: any) => ({
          id: dep.id,
          department: dep.department,
          mail: dep.mail,
        })),
      };

      await this.clientsService.saveClientContact(payload);
    } else {
      const payload = {
        ...value,
        departments: value.departments.map((dep: any) => ({
          department: dep.department,
          mail: dep.mail,
        })),
      };
      await this.clientsService.saveClientContact(payload);
    }

    await this.getData();
    this.closeContactModal();
  }

  async openDeleteContactModal(index: number) {
    this.submitError = '';
    this.deleteContactIndex = index;
    this.isDeleteContactModalVisible = true;
  }

  async confirmDeleteContact() {
    this.submitError = '';
    try {
      await this.doConfirmDeleteContact();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    }
  }

  private async doConfirmDeleteContact() {
    const contact = this.contacts.at(this.deleteContactIndex).value;
    if (contact.id) {
      await this.clientsService.deleteClientContact(contact.id);
      await this.getData();
    }
    this.deleteContactIndex = undefined;
    this.isDeleteContactModalVisible = false;
  }

  closeDeleteContactModal() {
    this.submitError = '';
    this.deleteContactIndex = undefined;
    this.isDeleteContactModalVisible = false;
  }

  async save() {
    for (let i = 0; i < this.contacts.length; i++) {
      if (this.contacts.at(i).dirty) {
        const contact = this.contacts.at(i).value;
        contact.client = Number(this.clientId);
        await this.clientsService.saveClientContact(contact);
      }
    }
    await this.getData();
  }

  get users(): FormArray {
    return this.usersForm.get('users') as FormArray;
  }

  private getFirstContactMail(contactId: number): string | undefined {
    const contact = this.configContactsTable.data.find((c) => c.id === contactId);
    const firstWithMail = contact?.departments?.find((d) => d?.mail);
    return firstWithMail?.mail;
  }

  async getUsersData() {
    if (this.clientId) {
      const data =
        (await this.clientsService.getContactUsers(Number(this.clientId))) ||
        [];
      const enrichedData = data.map((user) => {
        const contactId = user.contact?.id ?? user.contact;
        const firstMail = contactId ? this.getFirstContactMail(contactId) : undefined;
        return { ...user, mail: firstMail ?? user.mail };
      });
      this.users.clear();
      enrichedData.forEach((user) => {
        const laborModels = user.laborModelsAccess
          ? Array.isArray(user.laborModelsAccess)
            ? user.laborModelsAccess
                .map((id) => Number(id))
                .filter((id) => !isNaN(id))
            : []
          : [];

        const fiscalModels = user.fiscalModelsAccess
          ? Array.isArray(user.fiscalModelsAccess)
            ? user.fiscalModelsAccess
                .map((id) => Number(id))
                .filter((id) => !isNaN(id))
            : []
          : [];

        this.users.push(
          this.fb.group({
            id: [user.id],
            username: [user.username, Validators.required],
            password: [user.password, Validators.required],
            mail: [user.mail, [Validators.required, Validators.email]],
            contact: [user.contact],
            user: [user.client],
            hasAccess: [user.hasAccess || false],
            accessTo: [user.accessTo || []],
            laborModelsAccess: [laborModels],
            fiscalModelsAccess: [fiscalModels],
            accessToCircularLetters: [user.accessToCircularLetters || false],
            circularMail: [user.circularMail || ''],
            groupAccess: [user.groupAccess || []],
            newsletterAccess: [user.newsletterAccess || false],
            observations: [user.observations ?? ''],
          }),
        );
      });
      this.configUsersTable.data = enrichedData;
    }
  }

  openUserModal(index?: number) {
    this.submitError = '';
    this.editingUserIndex = index;
    if (index !== undefined) {
      const user = this.snapshotData
        ? this.configUsersTable.data[index]
        : this.users.at(index).value;

      // Extract contact id safely
      const contactId = user.contact?.id || user.contact;
      user.contact = contactId;

      // Load available emails from the contact using configContactsTable.data
      if (contactId) {
        const contact = this.configContactsTable.data.find(
          (c) => c.id === contactId,
        );
        if (
          contact &&
          contact.departments &&
          Array.isArray(contact.departments)
        ) {
          this.availableEmails = contact.departments
            .filter((dep) => dep && dep.mail)
            .map((dep) => ({
              label: dep.mail,
              value: dep.mail,
            }));
        } else {
          this.availableEmails = [];
        }
      } else {
        this.availableEmails = [];
      }

      this.userForm.patchValue(user);
    } else {
      // Generate password for new user
      function generateSecurePassword() {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lower = 'abcdefghijklmnopqrstuvwxyz';
        const numbers = '0123456789';
        const specials = '!@#$%^&*()_+-=<>?';
        let password = '';
        password += upper[Math.floor(Math.random() * upper.length)];
        password += lower[Math.floor(Math.random() * lower.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += specials[Math.floor(Math.random() * specials.length)];
        const all = upper + lower + numbers + specials;
        for (let i = 4; i < 8; i++) {
          password += all[Math.floor(Math.random() * all.length)];
        }
        // Mezclar
        return password
          .split('')
          .sort(() => 0.5 - Math.random())
          .join('');
      }
      const generatedPassword = generateSecurePassword();
      this.userForm.reset();
      this.userForm.patchValue({
        password: generatedPassword,
        hasAccess: false,
        accessToCircularLetters: false,
        newsletterAccess: false,
        observations: '',
      });
      // Username and email will be populated by valueChanges when contact is selected
    }
    this.isUserModalVisible = true;
    if (this.snapshotData) {
      this.userForm.disable();
    }
  }

  closeUserModal() {
    this.submitError = '';
    this.isUserModalVisible = false;
    this.editingUserIndex = undefined;
    this.availableEmails = [];
    this.userForm.reset();
  }

  async saveUser() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSaveUser();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSaveUser() {
    if (this.userForm.invalid) return;
    const value = this.userForm.value;
    value.client = Number(this.clientId);

    // Asegurar que los booleanos siempre tengan un valor (false si están vacíos)
    value.hasAccess = value.hasAccess || false;
    value.accessToCircularLetters = value.accessToCircularLetters || false;
    value.newsletterAccess = value.newsletterAccess || false;

    // Asegurar que los arrays estén inicializados y sean arrays
    value.accessTo = value.accessTo || [];
    value.laborModelsAccess = value.laborModelsAccess || [];
    value.fiscalModelsAccess = value.fiscalModelsAccess || [];
    value.groupAccess = value.groupAccess || [];

    if (this.editingUserIndex !== undefined && value.id) {
      await this.clientsService.updateContactUser(value.id, value);
    } else {
      await this.clientsService.saveContactUser(value);
    }

    await this.getUsersData();
    this.closeUserModal();
  }

  async openDeleteUserModal(index: number) {
    this.submitError = '';
    this.deleteUserIndex = index;
    this.isDeleteUserModalVisible = true;
  }

  async confirmDeleteUser() {
    this.submitError = '';
    try {
      await this.doConfirmDeleteUser();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    }
  }

  private async doConfirmDeleteUser() {
    const user = this.users.at(this.deleteUserIndex).value;
    if (user.id) {
      await this.clientsService.deleteContactUser(user.id);
      await this.getUsersData();
    }
    this.deleteUserIndex = undefined;
    this.isDeleteUserModalVisible = false;
  }

  closeDeleteUserModal() {
    this.submitError = '';
    this.deleteUserIndex = undefined;
    this.isDeleteUserModalVisible = false;
  }
}
